import React, {MouseEventHandler, useState} from 'react';
import { CANCEL } from '../ISRConstants';
import { EditIcon } from 'assests/GmsIcons';
import { ChevronLeft } from 'Components/DesignSystem/Iconography/Icons';
import Button from 'Components/DesignSystem/Button/Button';
import Typography from 'Components/DesignSystem/Typography/Typography';
import Skeleton from 'Components/DesignSystem/Skeleton/Skeleton';
import { FOCUS_RING } from 'Components/DesignSystem/focusRing';
import { useNavigate } from "react-router";
import { promoteISR } from '../Pages/DefineModel/ModelServices/ModalServices';
import { toast } from 'Components/DesignSystem/Toast/useToast';
import PromotionUIModel from "../shared/components/PopUp/PromotionUIModel";
import ConfirmModal from "./RuleComponents/CommitConfirmModal";
import { useISRContext } from '../Hooks/useISRContext';

// Cancel button is hidden in Guide models until it is wired up properly.
// Set to false to re-enable it. The back arrow handles navigation away from the model.
const HIDE_CANCEL_IN_GUIDE = true;

type TRenamedCategory = {
    category_id?: string;
    old_name: string;
    new_name: string;
}

type TBlockedPromotion = {
    modelName: string;
    renamedCategories: TRenamedCategory[];
    message: string;
}
type TMainPageHeaderProps = {
    componentId: string;
    isrName: string;
    showLoader: boolean;
    loaderMessage?: string;
    productId?: string | null | undefined;
    id?: string | null | undefined;
    onEditClick?: MouseEventHandler<HTMLButtonElement> | undefined;
    isGuideDetail?: boolean;
    onPromotionSuccess?: () => void;
    modelStatus?: string;
    widgetName?: string;
    isCurrentUserOwner?: boolean;
    onClone?: () => void;
    cloning?: boolean;
    hasDeletedRule?: boolean;
}

const MainPageHeader = ({
    componentId,
    isrName,
    showLoader,
    loaderMessage,
    productId,
    id,
    onEditClick,
    isGuideDetail,
    onPromotionSuccess,
    modelStatus,
    widgetName,
    isCurrentUserOwner,
    onClone,
    cloning,
    hasDeletedRule,
}: TMainPageHeaderProps) => {
    const navigate = useNavigate();
    const isrContext = useISRContext();
    const [disable, setDisable] = useState<boolean>(false);
    const [showPromotionDialog, setShowPromotionDialog] = useState<boolean>(false);
    const [blockedPromotion, setBlockedPromotion] = useState<TBlockedPromotion | null>(null);
    const [showRuleWarning, setShowRuleWarning] = useState<boolean>(false);
    const [ruleWarningMessage, setRuleWarningMessage] = useState<string>('');

    const path = isGuideDetail ? `/gms/guide/` : `/gms/isr`;

    const createPromotion = async (ids: string[]) => {
        if (productId === '') {
            toast.error('ProductId is required');
            return;
        }
        try {
            setDisable(true);
            toast.info('Promotion started ...');
            const response = await promoteISR(productId ? productId : id ?? '', ids);
            if (response?.done) {
                toast.success(response?.message);
                isrContext?.setRefreshCounter((c: number) => c + 1);

                if (onPromotionSuccess) {
                    onPromotionSuccess();
                }
            } else if (response?.error_type === 'renamed_categories') {
                // US 13052 / AC #1: promotion blocked on unresolved category rename.
                // Shown in a blocking modal (same design as the unsaved-changes /
                // commit confirm modal). The structured fields let the banner bold
                // the model and category names per the wireframe; the raw message
                // (minus the "Promotion blocked." prefix, which is the modal title)
                // is the fallback if they are ever missing.
                setBlockedPromotion({
                    modelName: response?.model_name ?? '',
                    renamedCategories: response?.renamed_categories ?? [],
                    message: (response?.message ?? '').replace(/^Promotion blocked\.\s*/, ''),
                });
            } else {
                toast.error(response?.message);
            }
        } catch (error) {
            // console.log("createPromotion ~ error:", error);
            toast.error('An error occurred during promotion');
        } finally {
            setDisable(false);
        }
    }

    const handlePromoteClick = () => {
        if (hasDeletedRule) {
            setRuleWarningMessage('A rule has been deleted and will not be included in this promotion.');
            setShowRuleWarning(true);
        } else {
            setShowPromotionDialog(true);
        }
    };

    return (
        <React.Fragment>
            {/*
                `surface`, was `shades-of-grey-10` (#fafafc) — 1.02:1 against the white
                page it sits on and against the tab strip directly below it, so it read
                as a faint grey band with no boundary rather than a distinct region.
                Nothing under it paints a ground either, so the bar is now continuous
                with the page. Set explicitly rather than left transparent (DS rule).
            */}
            <div id={componentId} className={`tw-flex tw-py-1.5 tw-items-center tw-bg-surface`}>
                <div id={`${componentId}-left-section`} className='tw-flex tw-items-center'>
                    {/* A real <button> now (was a role="button" div — no keyboard, no
                        focus ring), with DS ChevronLeft: the old BackArrowIcon hardcoded
                        stroke="#172B4D", invisible on the dark ground. Legacy testid and
                        id preserved — specs key off both. */}
                    <button type="button" aria-label="Back" data-testid="components-mainpageheader-div-1"
                        id={`${componentId}-back-btn`}
                        className={`tw-flex tw-items-center tw-pe-4 tw-text-ink-secondary hover:tw-text-ink ${FOCUS_RING}`}
                        onClick={() => navigate(path, { replace: true })}
                    >
                        <ChevronLeft size={20} />
                    </button>
                    {/* heading-2 + as="h1" — the enforced app-wide page-title pairing
                        (the 17th page to adopt it). Was a <label> at tw-text-black/24px
                        semibold: black ink is invisible in dark, and a label without a
                        control is the wrong element for the page's one h1.

                        § 314 — the title SHIMMERS on first load. `isrName` here is the
                        FETCHED project's name (`project.ISRName`), not the route's, so
                        until the request lands the h1 rendered empty while the loader
                        message beside Promote already printed the name. `tw-h-8` is the
                        32px line box `display` renders in, so the real title lands at
                        exactly the bar's height and the tab strip below never shifts.

                        Gated on `!isrName`, not on `showLoader` alone: that flag is also
                        true while SAVING, and replacing a title the user is looking at
                        with a grey bar on every autosave would be worse than no loader. */}
                    {showLoader && !isrName ? (
                        <Skeleton
                            variant="rect"
                            width="280px"
                            className="tw-h-8"
                            data-testid={`${componentId}-name-loading`}
                        />
                    ) : (
                        <Typography variant="display" as="h1" className="tw-text-ink" id={`${componentId}-name`}>
                            {isrName ?? ''}
                        </Typography>
                    )}
                    {/* TODO(restore): edit button was hidden for live models — restriction temporarily lifted so the owner can edit. Original condition: !(isGuideDetail && modelStatus === 'Live') */}
                    {/* Hidden while the title is a shimmer: renaming a project that has not
                        arrived opens a dialog over an empty name. Same reasoning as the
                        Data Model tab's Create Sub model button (§ 314). */}
                    {!(showLoader && !isrName) && (
                        <button aria-label="Edit" data-testid="components-mainpageheader-button-1"
                            id={`${componentId}-edit-btn`}
                            type="button"
                            onClick={onEditClick}
                            /* EditIcon already strokes currentColor — it was inheriting
                               the page's ambient (untokened) ink; this names it. */
                            className={`tw-flex tw-items-center tw-ms-2 tw-text-ink-secondary hover:tw-text-ink ${FOCUS_RING}`}
                        >
                            <EditIcon />
                        </button>
                    )}
                    {/* TODO(restore): "live model — clone to make changes" tag temporarily commented out so the owner can edit live models */}
                    {/* {isGuideDetail && modelStatus === 'Live' && (
                        <div className="tw-ml-6 tw-flex tw-items-center tw-gap-3 tw-border tw-border-[#1B6B3A] tw-rounded-lg tw-px-3 tw-py-2 tw-text-sm tw-text-[#1B6B3A] tw-bg-[#E8F5ED] tw-shrink-0">
                            <span className="tw-px-3 tw-py-1 tw-rounded-full tw-text-xs tw-font-semibold tw-bg-[#1B6B3A] tw-text-white tw-shrink-0">Live</span>
                            <div className="tw-flex tw-flex-col tw-leading-snug">
                                <span>This model is live in production.</span>
                                <span>Clone to make changes.</span>
                            </div>
                        </div>
                    )} */}
                    {isGuideDetail && isCurrentUserOwner === false && (
                        <span className="tw-ml-3 tw-flex tw-items-center tw-gap-1 tw-text-xs tw-text-tone-warning-ink">
                            <i className="fas fa-exclamation-triangle" />
                            <span>Not Editable</span>
                            <span className="tw-text-ink-secondary">Only the model owner can edit.</span>
                        </span>
                    )}
                </div>
                <div className='tw-flex-1'/>
                {/* `tw-items-center` — was the typo class `tw-item-center` (emits
                    nothing), which is why the loader label needed an `mt-2.5` nudge
                    to fake alignment. Real centring replaces both. */}
                <div id={`${componentId}-actions`} className='tw-flex tw-gap-2 tw-items-center tw-justify-end'>
                    {showLoader ?
                        <label id={`${componentId}-loader-msg`} className='tw-text-ink-secondary tw-text-sm tw-text-center'>
                            {`${loaderMessage}`}
                        </label>
                    : null}
                    {/* Live, Draft, Rollback hidden until functionality is confirmed */}
                    {!(isGuideDetail && HIDE_CANCEL_IN_GUIDE) && (
                    <Button data-testid="components-mainpageheader-button-2"
                        id={`${componentId}-cancel-btn`}
                        variant="secondary"
                        onClick={() => navigate('/gms/isr', { replace: true })}
                    >
                        {CANCEL}
                    </Button>
                    )}
                    {/* DS primary, was the global `.button-style` (#091caa + white
                        literals) with hand-rolled disabled classes — the variant
                        brings the themed disabled state with it. */}
                    <Button data-testid="components-mainpageheader-button-3"
                        id={`${componentId}-promote-btn`}
                        variant="primary"
                        onClick={handlePromoteClick}
                        disabled={disable}
                    >
                        {'Promote'}
                    </Button>
                </div>
            </div>

            {showRuleWarning && (
                <ConfirmModal
                    id="rule-warning-modal"
                    title="Warning: Rule changes detected"
                    message={<span className='tw-whitespace-pre-line'>{ruleWarningMessage}</span>}
                    confirmText="Proceed with Promotion"
                    cancelText="Cancel"
                    onClose={() => setShowRuleWarning(false)}
                    onConfirm={() => {
                        setShowRuleWarning(false);
                        setShowPromotionDialog(true);
                    }}
                />
            )}

            {/* PromotionUIModel renders its own DS Dialog (2026-09-09); the legacy popup wrapper is gone here. */}
            {showPromotionDialog ?
                <PromotionUIModel projectId={productId ?? ''} projectName={isrName} onSuccess={(ids) => {
                    createPromotion(ids);
                    setShowPromotionDialog(false);
                }} onClose={() => setShowPromotionDialog(false)}/>
            : null}

            {blockedPromotion !== null && (
                <ConfirmModal
                    id="promotion-blocked-modal"
                    title="Promotion blocked"
                    message={blockedPromotion.renamedCategories.length > 0 ? (
                        <span>
                            <b>{blockedPromotion.modelName}</b> references renamed category(s) which must be updated before promotion:
                            {blockedPromotion.renamedCategories.map((renamedCategory, index) => (
                                <React.Fragment key={renamedCategory.category_id ?? index}>
                                    <br />Category: <b>{renamedCategory.old_name}</b> → <b>{renamedCategory.new_name}</b>
                                </React.Fragment>
                            ))}
                        </span>
                    ) : (
                        <span className='tw-whitespace-pre-line'>{blockedPromotion.message}</span>
                    )}
                    confirmText="OK"
                    onClose={() => setBlockedPromotion(null)}
                    onConfirm={() => setBlockedPromotion(null)}
                />
            )}

        </React.Fragment>
    );
}

export default MainPageHeader;